import { type ComponentRef, type RefObject, useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { MathUtils, PerspectiveCamera, Vector3 } from 'three';
import type { OrbitControls } from '@react-three/drei';
import { resolveCameraPose, type OrbitControlLimits } from './cameraPoses';
import { STAGE_MODES, type StageMode } from '../model/vehicleDriving';
import type { CarModel } from '../types';

/**
 * drei <OrbitControls>의 인스턴스 타입.
 * 뷰어와 카메라 리그가 같은 ref를 주고받아야 해서 여기 한 번만 적어 둡니다.
 */
export type ShowroomOrbitControls = ComponentRef<typeof OrbitControls>;

const TRANSITION_DAMPING = 5;
const ARRIVAL_DISTANCE = 0.01;
const ARRIVAL_FOV = 0.05;

function dampVector(current: Vector3, goal: Vector3, delta: number): void {
  current.x = MathUtils.damp(current.x, goal.x, TRANSITION_DAMPING, delta);
  current.y = MathUtils.damp(current.y, goal.y, TRANSITION_DAMPING, delta);
  current.z = MathUtils.damp(current.z, goal.z, TRANSITION_DAMPING, delta);
}

function applyControlLimits(controls: ShowroomOrbitControls, limits: OrbitControlLimits): void {
  controls.minDistance = limits.minDistance;
  controls.maxDistance = limits.maxDistance;
  controls.minPolarAngle = limits.minPolarAngle;
  controls.maxPolarAngle = limits.maxPolarAngle;
  controls.enableZoom = limits.enableZoom;
  controls.rotateSpeed = limits.rotateSpeed;
}

export interface CameraRigProps {
  viewMode: string;
  model: CarModel;
  stageMode: StageMode;
  controlsRef: RefObject<ShowroomOrbitControls | null>;
}

export function CameraRig({ viewMode, model, stageMode, controlsRef }: CameraRigProps) {
  /*
   * 이 씬의 카메라는 PerspectiveCamera입니다(ShowroomCanvas가 fov를 주고 만듭니다).
   * R3F의 state.camera는 Orthographic도 담을 수 있는 넓은 타입이라, fov를 만지려면
   * 여기서 좁혀야 합니다.
   */
  const camera = useThree((state) => state.camera as PerspectiveCamera);
  const poseRef = useRef(resolveCameraPose(viewMode, model));
  const isTransitioningRef = useRef(true);
  const previousStageModeRef = useRef(stageMode);
  const goalPosition = useRef(new Vector3()).current;
  const goalTarget = useRef(new Vector3()).current;

  // 모델을 바꾸면 치수가 달라져 목표 pose도 달라집니다. 그래서 보기 모드와 같은 취급입니다.
  // resolveCameraPose는 매번 새 객체를 반환하므로 pose 자체를 의존성에 넣으면 매 렌더마다
  // 전환이 재시작됩니다. 그래서 식별자(model.id)만 의존성으로 둡니다.
  const modelId = model.id;

  useEffect(() => {
    /*
     * 주행 중에는 물러납니다. 추격 카메라(three/chaseCamera)가 매 프레임 카메라를
     * 직접 쓰기 때문에, 여기서 목표 pose로 damp하면 두 쪽이 같은 프레임에 카메라를
     * 밀고 당겨서 화면이 떱니다.
     *
     * 주행 진입은 CarDriver가 1,188m 밖의 출발선으로 즉시 옮깁니다. 반대로 보기
     * 모드로 나올 때도 곧바로 현재 보기 pose로 복귀해야 합니다. 예전처럼 damp하면
     * 차량은 원점에 있는데 카메라만 1km를 가로질러 1초 넘게 날아옵니다.
     */
    const wasDriving = previousStageModeRef.current === STAGE_MODES.DRIVING;
    previousStageModeRef.current = stageMode;

    if (stageMode === STAGE_MODES.DRIVING) {
      isTransitioningRef.current = false;
      return;
    }

    const pose = resolveCameraPose(viewMode, model);
    poseRef.current = pose;
    const controls = controlsRef.current;

    if (wasDriving) {
      camera.position.fromArray(pose.position);
      goalTarget.fromArray(pose.target);
      camera.fov = pose.fov;
      camera.updateProjectionMatrix();
      camera.lookAt(goalTarget);

      if (controls !== null) {
        controls.target.copy(goalTarget);
        applyControlLimits(controls, pose.controls);
        controls.enabled = true;
        controls.update();
      }
      isTransitioningRef.current = controls === null;
      return;
    }

    isTransitioningRef.current = true;
    if (controls !== null) {
      controls.enabled = false;
    }
    // model 객체는 매 렌더마다 바뀔 수 있어 식별자인 modelId만 의존합니다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera, viewMode, modelId, stageMode, controlsRef, goalTarget]);

  useFrame((_, delta) => {
    const controls = controlsRef.current;
    if (!isTransitioningRef.current || !controls) {
      return;
    }
    const pose = poseRef.current;
    goalPosition.fromArray(pose.position);
    goalTarget.fromArray(pose.target);

    dampVector(camera.position, goalPosition, delta);
    dampVector(controls.target, goalTarget, delta);
    camera.fov = MathUtils.damp(camera.fov, pose.fov, TRANSITION_DAMPING, delta);
    camera.updateProjectionMatrix();
    camera.lookAt(controls.target);

    const hasArrived =
      camera.position.distanceTo(goalPosition) < ARRIVAL_DISTANCE &&
      controls.target.distanceTo(goalTarget) < ARRIVAL_DISTANCE &&
      Math.abs(camera.fov - pose.fov) < ARRIVAL_FOV;
    if (!hasArrived) {
      return;
    }

    camera.position.copy(goalPosition);
    controls.target.copy(goalTarget);
    camera.fov = pose.fov;
    camera.updateProjectionMatrix();
    applyControlLimits(controls, pose.controls);
    controls.enabled = true;
    controls.update();
    isTransitioningRef.current = false;
  });

  return null;
}
